import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Alert, Button } from '@mui/material';
import { DateTime } from 'luxon';
import { OFFICE_PHONE, LATE_MINUTE_HOUR, GERMAN_TIMEZONE } from '../../../constants';
import { TRANSFER } from '../../models/transfer';
import { nowInGermany } from '../../../common/utils/serverClock';

// Phone-only gate: booked at/after LATE_MINUTE_HOUR.FROM (Munich) for a trip before
// LATE_MINUTE_HOUR.TO tomorrow -> the continue button gives way to "call us".
const checkOrderLate = (now) => now.hour >= LATE_MINUTE_HOUR.FROM;

const checkBookingEarlyNextDay = (dateTime, now) =>
    dateTime < now.plus({ days: 1 }).set({ hour: LATE_MINUTE_HOUR.TO }).startOf('hour');

const checkLastMinuteTransfer = (transfer) => {
    let bookingDateTime;

    switch (transfer.typeId) {
        case TRANSFER.TO_MUNICH:
        case TRANSFER.TO_NUREMBERG:
            bookingDateTime = DateTime.fromISO(transfer.flight.date + 'T' + transfer.arrivalTime, {
                zone: GERMAN_TIMEZONE,
            });
            break;

        case TRANSFER.FROM_MUNICH:
        case TRANSFER.FROM_NUREMBERG:
            bookingDateTime = DateTime.fromISO(transfer.flight.date + 'T' + transfer.flight.time, {
                zone: GERMAN_TIMEZONE,
            });
            break;

        case TRANSFER.SPECIAL:
            bookingDateTime = DateTime.fromISO(transfer.tripDate + 'T' + transfer.tripTime, { zone: GERMAN_TIMEZONE });
            break;

        default:
            return false;
    }

    const now = nowInGermany();
    return checkOrderLate(now) && checkBookingEarlyNextDay(bookingDateTime, now);
};

const isLastMinuteBooking = (transfer1, transfer2) => {
    return [transfer1, transfer2].filter(Boolean).some(checkLastMinuteTransfer);
};

const OrderActions = ({ firstTransfer, secondTransfer, isBookingsValid, children }) => {
    const { t } = useTranslation();

    if (isBookingsValid && isLastMinuteBooking(firstTransfer, secondTransfer)) {
        return (
            <Alert
                variant="filled"
                icon={false}
                sx={{
                    marginLeft: '42px',
                }}
                severity="warning"
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ mr: 1, lineHeight: 1.4, color: 'text.primary' }}>
                        {t('shortTermBooking')}
                    </Typography>
                    <Button
                        type="button"
                        variant="outlined"
                        sx={{
                            flexShrink: 0,
                            color: 'text.primary',
                            borderColor: 'text.primary',
                        }}
                        href={`tel:${OFFICE_PHONE}`}
                    >
                        <Box sx={{ whiteSpace: 'nowrap' }}>{OFFICE_PHONE}</Box>
                    </Button>
                </Box>
            </Alert>
        );
    }

    return children;
};

export { OrderActions };
